import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ComponentPermission } from '../../../../component.permission';
import { Constant } from '../../../../shared/common/constant';
import { DateUtils } from '../../../../shared/common/date-utils';
import { StringUtils } from '../../../../shared/common/string-utils';
import { SearchRequest } from '../../../../shared/models/request/search-request';
import { BuyerSearchRequest } from '../../../../shared/models/request/buyer/buyer-search-request';
import { Triple } from '../../../../shared/models/request/triple';
import { Category } from '../../../../shared/models/response/category';
import { RestResult } from '../../../../shared/models/response/rest-result';
import { Buyer } from '../../../../shared/models/response/buyer/buyer';
import { City } from '../../../../shared/models/response/address/city';
import { District } from '../../../../shared/models/response/address/district';
import { Street } from '../../../../shared/models/response/address/street';
import { Postcode } from '../../../../shared/models/response/postcode';
import { Company } from '../../../../shared/models/response/prospect/company';
import { TaskStatus } from '../../../../shared/models/response/task-status';
import { TaskType } from '../../../../shared/models/response/task-type';
import { User } from '../../../../shared/models/response/user';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { BuyerCreateV2Component } from '../../../../shared/module/buyer-create-v2/buyer-create-v2.component';
import { ExportAddressComponent } from '../../../../shared/module/export-address/export-address.component';
import { AuthService } from '../../../../shared/services/auth.service';
import { BuyerService } from '../../../../shared/services/buyer.service';
import { CategoryService } from '../../../../shared/services/category.service';
import { CommonService } from '../../../../shared/services/common.service';
import { CompanyService } from '../../../../shared/services/company.service';
import { LocalService } from '../../../../shared/services/local.service';
import { TaskStatusService } from '../../../../shared/services/task-status.service';
import { TaskTypeService } from '../../../../shared/services/task-type.service';
import { TemplateService } from '../../../../shared/services/template.service';
import { PostcodeService } from '../../../../shared/services/postcode.service';

@Component({
  selector: 'ngx-buyer-list-v2',
  templateUrl: './buyer-list-v2.component.html',
  styleUrls: ['./buyer-list-v2.component.scss'],
})
export class BuyerListV2Component implements OnInit, AfterViewInit {
  @ViewChild(BuyerCreateV2Component) buyerCreateV2: BuyerCreateV2Component;
  @ViewChild('tenantBuyerCreateV2') tenantBuyerCreateV2: any;

  ComponentPermission = ComponentPermission;
  StringUtils = StringUtils;

  buyers: Buyer[] = [];
  selectedBuyer: Buyer;
  selectedBuyerIds: number[] = [];
  selectedBuyerRecordIds: number[] = [];
  cities: City[] = [];
  companies: Company[] = [];
  taskTypes: TaskType[] = [];
  taskStatuses: TaskStatus[] = [];
  currentUser: User;

  searchRequest = new BuyerSearchRequest();
  searchMode = false;
  searchModeBuyer: Buyer;
  returnToListAfterSearch = false;
  searchFromEnqDate: string;
  searchToEnqDate: string;
  areaDistricts: District[] = [];
  areaStreets: Street[] = [];
  livingDistricts: District[] = [];
  livingStreets: Street[] = [];
  propertyTypeOptions = Object.keys(Constant.PROPERTY_TYPE).map(key => ({ key, label: Constant.PROPERTY_TYPE[key] }));
  propertySpecOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  selectedCategories: Category[] = [];
  categoryName: string;
  filterCategory$: Observable<Category[]>;
  filterPreferredPostcode$: Observable<Postcode[]>;
  preferredPostcodeInput = '';

  selectedYear: number;
  selectedMonth: number;
  selectedWeek: number;
  selectedDay: number;
  years: number[] = [];
  months: number[] = [];
  weeks: number[] = [];
  days: number[] = [];

  showAdvance = true;
  isDisplay = false;
  isLoading = false;
  allChecked = false;
  isBulkUpdateMode = false;
  totalRecords = 0;

  currentPage = 1;
  pageSize = Constant.PAGE_SIZE;
  totalPages = 0;
  pages = [];
  buyerMode: 'B' | 'T' = 'B';

  classOptions = ['F', 'I', 'VB', 'D'];
  statusOptions = Constant.ACTIVE_TYPE;
  rateOptions = [
    { value: 0, label: '0' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
  ];

  constructor(private buyerService: BuyerService,
              private authService: AuthService,
              private categoryService: CategoryService,
              private companyService: CompanyService,
              private taskTypeService: TaskTypeService,
              private taskStatusService: TaskStatusService,
              private templateService: TemplateService,
              private postcodeService: PostcodeService,
              private localService: LocalService,
              private commonService: CommonService,
              private dialogService: NbDialogService,
              private route: ActivatedRoute) {
  }

  ngAfterViewInit(): void {
    if (this.selectedBuyer) {
      this.activeBuyerCreateV2()?.setBuyer(this.selectedBuyer);
    } else {
      this.clearSelectedBuyer();
    }
  }

  ngOnInit(): void {
    this.currentUser = this.authService.currentUser;
    this.initYears();
    this.initAutoComplete();
    this.loadForEditor();
    this.searchRequest.officeId = this.currentUser?.office?.officeId;
    this.searchRequest.buyerCode = this.buyerMode;
    this.search();
  }

  initYears() {
    for (let year = 2000; year <= 3000; year++) {
      this.years.push(year);
    }
  }

  initAutoComplete() {
    this.filterCategory$ = new Observable((observer: any) => {
      observer.next(this.categoryName);
    }).pipe(
      mergeMap((query: string) => this.searchCategory(query)),
    );
    this.filterPreferredPostcode$ = new Observable((observer: any) => {
      observer.next(this.preferredPostcodeInput);
    }).pipe(
      switchMap((query: string) => this.searchPreferredPostcodes(query)),
    );
  }

  loadForEditor() {
    const request = new SearchRequest();
    request.offset = 0;
    request.limit = 10000;
    request.orders = [{
      left: 'name',
      right: 'asc',
    }];

    this.localService.cities().subscribe((result: City[]) => {
      this.cities = result;
    });
    this.companyService.search(request).subscribe((result: RestResult) => {
      this.companies = result?.data || [];
    });
    this.taskTypeService.search(request).subscribe((result: RestResult) => {
      this.taskTypes = result?.data || [];
    });
    this.taskStatusService.search(request).subscribe((result: RestResult) => {
      this.taskStatuses = result?.data || [];
    });
  }

  search(page = 1) {
    this.isLoading = true;
    this.currentPage = page;
    this.searchRequest.offset = (page - 1) * this.pageSize;
    this.searchRequest.limit = this.pageSize;
    this.searchRequest.officeId = this.currentUser?.office?.officeId;

    this.buyerService.getBuyerListV2(this.searchRequest).subscribe((result: RestResult) => {
      this.buyers = result?.data || [];
      this.buyerCreateV2?.setBuyers(this.buyers);
      this.tenantBuyerCreateV2?.setBuyers(this.buyers);
      this.totalRecords = Number(result?.metaData?.total || 0);
      this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
      this.generatePagination(page);
      if (this.returnToListAfterSearch) {
        this.returnToListAfterSearch = false;
        this.clearSelectedBuyer();
      } else if (this.searchMode) {
        this.selectedBuyer = undefined;
        this.isDisplay = true;
      } else if (this.buyers.length > 0) {
        const selectedId = this.selectedBuyer?.buyerId || Number(this.route.snapshot.queryParams['selectedBuyerId']);
        const nextBuyer = this.buyers.find((item) => item.buyerId === selectedId);
        if (nextBuyer) {
          this.selectBuyer(nextBuyer);
        } else {
          this.clearSelectedBuyer();
        }
      } else {
        this.clearSelectedBuyer();
      }
      this.syncSelectionState();
      this.isLoading = false;
    }, () => {
      this.buyers = [];
      this.isLoading = false;
    });
  }

  generatePagination(current: number) {
    const start = Math.max(1, current - 2);
    const end = Math.min(this.totalPages, start + 4);
    this.pages = [];
    for (let page = start; page <= end; page++) {
      this.pages.push(page);
    }
  }

  pageChange(page?: number, isNext?: boolean) {
    if (page) {
      this.search(page);
      return;
    }
    if (isNext === true && this.currentPage < this.totalPages) {
      this.search(this.currentPage + 1);
      return;
    }
    if (isNext === false && this.currentPage > 1) {
      this.search(this.currentPage - 1);
    }
  }

  onSearchButtonClick() {
    this.search(1);
  }

  openDetailSearch() {
    this.searchModeBuyer = this.selectedBuyer;
    this.searchMode = true;
    this.isDisplay = true;
  }

  executeDetailSearch() {
    this.searchRequest.fromEnqDate = this.toStartOfDay(this.searchFromEnqDate);
    this.searchRequest.toEnqDate = this.toEndOfDay(this.searchToEnqDate);
    if (window.innerWidth <= 991) {
      this.returnToListAfterSearch = true;
      this.searchMode = false;
      this.isDisplay = false;
    }
    this.search(1);
  }

  clearDetailSearch() {
    const officeId = this.currentUser?.office?.officeId;
    const buyerCode = this.buyerMode;
    this.searchRequest = new BuyerSearchRequest();
    this.searchRequest.officeId = officeId;
    this.searchRequest.buyerCode = buyerCode;
    this.selectedCategories = [];
    this.categoryName = '';
    this.searchFromEnqDate = undefined;
    this.searchToEnqDate = undefined;
    this.selectedYear = undefined;
    this.selectedMonth = undefined;
    this.selectedWeek = undefined;
    this.selectedDay = undefined;
    this.areaDistricts = [];
    this.areaStreets = [];
    this.livingDistricts = [];
    this.livingStreets = [];
    this.preferredPostcodeInput = '';
  }

  cancelDetailSearch() {
    this.searchMode = false;
    this.selectedBuyer = this.searchModeBuyer;
    this.searchModeBuyer = undefined;
    this.isDisplay = !!this.selectedBuyer;
    if (this.selectedBuyer) {
      this.activeBuyerCreateV2()?.setBuyer(this.selectedBuyer);
    }
  }

  onAreaPostcodeChange(postcode: string) {
    this.searchRequest.areaDistrictId = undefined;
    this.searchRequest.areaStreetId = undefined;
    this.areaStreets = [];
    this.loadDistricts(postcode, districts => this.areaDistricts = districts);
  }

  onAreaDistrictChange(districtId: number) {
    this.searchRequest.areaStreetId = undefined;
    this.loadStreets(districtId, streets => this.areaStreets = streets);
  }

  onLivingPostcodeChange(postcode: string) {
    this.searchRequest.livingDistrictId = undefined;
    this.searchRequest.livingStreetId = undefined;
    this.livingStreets = [];
    this.loadDistricts(postcode, districts => this.livingDistricts = districts);
  }

  onLivingDistrictChange(districtId: number) {
    this.searchRequest.livingStreetId = undefined;
    this.loadStreets(districtId, streets => this.livingStreets = streets);
  }

  onSearchBuyerCodeChange(buyerCode: string) {
    if (buyerCode !== 'T') {
      return;
    }
    this.searchRequest.livingPostcode = undefined;
    this.searchRequest.livingDistrictId = undefined;
    this.searchRequest.livingStreetId = undefined;
    this.searchRequest.livingUnit = undefined;
    this.searchRequest.livingNumberFrom = undefined;
    this.livingDistricts = [];
    this.livingStreets = [];
  }

  private loadDistricts(postcode: string, callback: (districts: District[]) => void) {
    if (!postcode || String(postcode).length < 3) {
      callback([]);
      return;
    }
    this.localService.districtByPostcode(Number(postcode)).subscribe(result => callback(result || []));
  }

  private loadStreets(districtId: number, callback: (streets: Street[]) => void) {
    if (!districtId) {
      callback([]);
      return;
    }
    this.localService.streets(districtId).subscribe(result => callback(result || []));
  }

  private toStartOfDay(value: string): number {
    return value ? moment(value, 'YYYY-MM-DD').startOf('day').valueOf() : undefined;
  }

  private toEndOfDay(value: string): number {
    return value ? moment(value, 'YYYY-MM-DD').endOf('day').valueOf() : undefined;
  }

  setBuyerMode(mode: 'B' | 'T') {
    if (this.buyerMode === mode) {
      return;
    }
    this.buyerMode = mode;
    this.searchRequest.buyerCode = mode;
    this.clearSelection();
    this.clearSelectedBuyer();
    this.search(1);
  }

  onYearChange() {
    this.selectedMonth = undefined;
    this.selectedWeek = undefined;
    this.selectedDay = undefined;
    this.months = [];
    this.weeks = [];
    this.days = [];

    if (this.selectedYear) {
      const first = DateUtils.getFirstDayOfYear(this.selectedYear);
      const last = DateUtils.getLastDayOfYear(this.selectedYear);
      this.searchRequest.fromEnqDate = first.valueOf();
      this.searchRequest.toEnqDate = last.valueOf();
      this.months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    } else {
      this.searchRequest.fromEnqDate = undefined;
      this.searchRequest.toEnqDate = undefined;
    }
  }

  onMonthChange() {
    this.selectedWeek = undefined;
    this.selectedDay = undefined;
    this.days = [];
    this.weeks = [];

    if (this.selectedMonth) {
      const firstWeek = DateUtils.getFirstWeekOfMonth(this.selectedMonth - 1, this.selectedYear);
      const lastWeek = DateUtils.getLastWeekOfMonth(this.selectedMonth - 1, this.selectedYear);
      for (let week = firstWeek; week <= lastWeek; week++) {
        this.weeks.push(week);
      }
      const first = DateUtils.getFirstDayOfMonth(this.selectedMonth - 1, this.selectedYear);
      const last = DateUtils.getLastDayOfMonth(this.selectedMonth - 1, this.selectedYear);
      this.searchRequest.fromEnqDate = first.valueOf();
      this.searchRequest.toEnqDate = last.valueOf();
    } else if (this.selectedYear) {
      const first = DateUtils.getFirstDayOfYear(this.selectedYear);
      const last = DateUtils.getLastDayOfYear(this.selectedYear);
      this.searchRequest.fromEnqDate = first.valueOf();
      this.searchRequest.toEnqDate = last.valueOf();
    }
  }

  onWeekChange() {
    this.selectedDay = undefined;
    this.days = [];

    if (this.selectedWeek) {
      const first = DateUtils.getFirstDayOfWeek(this.selectedWeek, this.selectedYear);
      const last = DateUtils.getLastDayOfWeek(this.selectedWeek, this.selectedYear);
      for (let tick = first.valueOf(); tick <= last.valueOf(); tick += 86400000) {
        this.days.push(new Date(tick).getDate());
      }
      this.searchRequest.fromEnqDate = first.valueOf();
      this.searchRequest.toEnqDate = last.valueOf();
    } else if (this.selectedMonth) {
      this.onMonthChange();
    }
  }

  onDayChange() {
    if (this.selectedDay) {
      const start = moment().year(this.selectedYear).month((this.selectedMonth || 1) - 1).date(this.selectedDay).startOf('day');
      const end = moment().year(this.selectedYear).month((this.selectedMonth || 1) - 1).date(this.selectedDay).endOf('day');
      this.searchRequest.fromEnqDate = start.valueOf();
      this.searchRequest.toEnqDate = end.valueOf();
    } else if (this.selectedWeek) {
      this.onWeekChange();
    }
  }

  selectCategory(event: any) {
    const category = event.item as Category;
    if (!category || this.selectedCategories.find((item) => item.categoryId === category.categoryId)) {
      this.categoryName = '';
      return;
    }
    this.selectedCategories.push(category);
    if (!this.searchRequest.categoryIds) {
      this.searchRequest.categoryIds = [];
    }
    this.searchRequest.categoryIds.push(category.categoryId);
    this.categoryName = '';
  }

  removeCategory(category: Category) {
    this.selectedCategories = this.selectedCategories.filter((item) => item.categoryId !== category.categoryId);
    this.searchRequest.categoryIds = (this.searchRequest.categoryIds || []).filter((id) => id !== category.categoryId);
  }

  selectPreferredPostcode(event: any) {
    const postcode = String(event?.item?.code || '').trim();
    if (!/^\d{4}$/.test(postcode)) {
      return;
    }
    const preferredPostcodes = this.searchRequest.preferredPostcodes || [];
    if (preferredPostcodes.indexOf(postcode) < 0) {
      this.searchRequest.preferredPostcodes = [...preferredPostcodes, postcode];
    }
    this.preferredPostcodeInput = '';
  }

  removePreferredPostcode(postcode: string) {
    this.searchRequest.preferredPostcodes = (this.searchRequest.preferredPostcodes || [])
      .filter(item => item !== postcode);
  }

  searchPreferredPostcodes(keyword: string): Observable<Postcode[]> {
    const normalized = String(keyword || '').replace(/\D/g, '').slice(0, 4);
    if (normalized.length < 2) {
      return of([]);
    }
    const request = new SearchRequest();
    request.offset = 0;
    request.limit = 250;
    request.conditionType = 'AND';
    request.conditions = [{ left: 'code', middle: 'like', right: normalized } as Triple];
    request.orders = [{ left: 'code', right: 'asc' }];
    return this.postcodeService.search(request).pipe(
      map((result: RestResult) => (result?.data || [])
        .filter(postcode => String(postcode?.code || '').startsWith(normalized))
        .slice(0, 10)),
      catchError(() => of([])),
    );
  }

  searchCategory(query: string) {
    const request = new SearchRequest();
    request.offset = 0;
    request.limit = 20;
    request.conditionType = 'AND';
    request.conditions = [];
    if (StringUtils.isNotEmpty(query)) {
      request.conditions.push({
        left: 'name',
        middle: 'like',
        right: query,
      } as Triple);
    }
    request.orders = [{
      left: 'name',
      right: 'asc',
    }];
    return this.categoryService.search(request).pipe(
      map((result: RestResult) => result?.data || []),
    );
  }

  openNewBuyer() {
    this.searchMode = false;
    this.searchModeBuyer = undefined;
    this.isBulkUpdateMode = false;
    this.isDisplay = true;
    this.selectedBuyer = undefined;
    this.buyerCreateV2?.new();
  }

  selectBuyer(buyer: Buyer) {
    if (!buyer) {
      return;
    }
    this.searchMode = false;
    this.searchModeBuyer = undefined;
    this.selectedBuyer = buyer;
    this.isBulkUpdateMode = false;
    this.isDisplay = true;
    this.buyerCreateV2?.setBuyers(this.buyers);
    this.tenantBuyerCreateV2?.setBuyers(this.buyers);
    this.activeBuyerCreateV2()?.setBuyer(buyer);
  }

  navigateBuyer(value: any) {
    if (!value?.currentBuyerId) {
      return;
    }
    this.selectedBuyer = this.buyers?.find((buyer) => buyer.buyerId === value.currentBuyerId);
    if (!this.selectedBuyer) {
      return;
    }
    this.isDisplay = true;
    this.activeBuyerCreateV2()?.setBuyer(this.selectedBuyer);
  }

  clearSelectedBuyer() {
    this.selectedBuyer = undefined;
    this.isBulkUpdateMode = false;
    this.isDisplay = this.searchMode;
    this.buyerCreateV2?.setBuyer(null);
    this.tenantBuyerCreateV2?.setBuyer(null);
  }

  isTenantBuyer(buyer: Buyer = this.selectedBuyer): boolean {
    return buyer?.buyerCode === 'T';
  }

  isTenantEditorActive(): boolean {
    return this.isBulkUpdateMode ? this.buyerMode === 'T' : this.isTenantBuyer();
  }

  private activeBuyerCreateV2(): any {
    return this.isTenantEditorActive() ? this.tenantBuyerCreateV2 : this.buyerCreateV2;
  }

  toggleBuyerSelection(buyer: Buyer, checked: boolean) {
    if (checked) {
      if (!this.selectedBuyerIds.includes(buyer.customer?.customerId)) {
        this.selectedBuyerIds.push(buyer.customer?.customerId);
      }
      if (!this.selectedBuyerRecordIds.includes(buyer.buyerId)) {
        this.selectedBuyerRecordIds.push(buyer.buyerId);
      }
    } else {
      this.selectedBuyerIds = this.selectedBuyerIds.filter((id) => id !== buyer.customer?.customerId);
      this.selectedBuyerRecordIds = this.selectedBuyerRecordIds.filter((id) => id !== buyer.buyerId);
    }
    this.syncSelectionState();
  }

  toggleAllSelection(checked: boolean) {
    if (checked) {
      this.selectedBuyerIds = this.buyers
        .map((buyer) => buyer.customer?.customerId)
        .filter((id) => !!id);
      this.selectedBuyerRecordIds = this.buyers
        .map((buyer) => buyer.buyerId)
        .filter((id) => !!id);
    } else {
      this.selectedBuyerIds = [];
      this.selectedBuyerRecordIds = [];
    }
    this.syncSelectionState();
  }

  syncSelectionState() {
    const selectableIds = this.buyers.map((buyer) => buyer.customer?.customerId).filter((id) => !!id);
    this.allChecked = selectableIds.length > 0 && selectableIds.every((id) => this.selectedBuyerIds.includes(id));
  }

  isSelected(buyer: Buyer) {
    return this.selectedBuyerIds.includes(buyer.customer?.customerId);
  }

  clearSelection() {
    this.selectedBuyerIds = [];
    this.selectedBuyerRecordIds = [];
    this.syncSelectionState();
    if (this.isBulkUpdateMode) {
      this.clearSelectedBuyer();
    }
  }

  applyAll() {
    if (this.selectedBuyerRecordIds.length <= 1) {
      return;
    }
    this.searchMode = false;
    this.searchModeBuyer = undefined;
    this.selectedBuyer = undefined;
    this.isDisplay = true;
    this.isBulkUpdateMode = true;
    this.activeBuyerCreateV2()?.startBulkUpdate(this.selectedBuyerRecordIds, this.buyerMode);
  }

  exportMobile() {
    const request = this.buildExportRequest();
    this.buyerService.getPhone(request).subscribe((result: RestResult) => {
      if (!result?.data) {
        return;
      }
      const templateSearch = new SearchRequest();
      templateSearch.conditions = [{
        left: 'type',
        middle: '=',
        right: 'SMS',
      }];
      this.templateService.search(templateSearch).subscribe((templateResult: RestResult) => {
        this.dialogService.open(ExportDialogComponent, {
          context: {
            buyerMobile: result.data,
            templates: templateResult.data,
            adminNumber: this.currentUser?.office?.phone,
            exportType: 'MOBILE',
          },
        });
      });
    });
  }

  exportAddress() {
    const data = this.selectedBuyerIds.length > 0
      ? this.buyers.filter((buyer) => this.selectedBuyerIds.includes(buyer.customer?.customerId))
      : this.buyers;
    this.dialogService.open(ExportAddressComponent, {
      context: {
        data: data as any,
      },
    });
  }

  buildExportRequest(): BuyerSearchRequest {
    const request = JSON.parse(JSON.stringify(this.searchRequest));
    request.offset = this.selectedBuyerIds.length > 0 ? 0 : (this.currentPage - 1) * this.pageSize;
    request.limit = this.pageSize;
    if (this.selectedBuyerIds.length > 0) {
      request.customerIds = this.selectedBuyerIds;
    }
    return request;
  }

  editDone(result: any) {
    if (result) {
      if (this.isBulkUpdateMode) {
        this.clearSelection();
        this.clearSelectedBuyer();
      }
      this.search(this.currentPage);
    }
  }

  newDone(result: any) {
    if (result) {
      this.search(1);
    }
  }

  reserveBuyer(result: any) {
    if (result) {
      this.search(this.currentPage);
    }
  }

  ownSet(result: any) {
    if (result) {
      this.search(this.currentPage);
    }
  }

  cancel() {
    this.clearSelectedBuyer();
  }

  getAddress(buyer: Buyer): string {
    return StringUtils.getShortAddress(buyer?.prospect?.address || buyer?.address);
  }

  getName(buyer: Buyer): string {
    return StringUtils.getFullName(buyer as any).trim();
  }

  getTags(buyer: Buyer): string {
    return StringUtils.getAllTags(buyer?.categories);
  }

  getClassification(buyer: Buyer): string {
    const status = this.getStatusCode(buyer);
    const prefix = buyer?.buyerCode;
    if (!status) {
      return `${prefix}.${buyer?.rate || 0}`;
    }
    return `${prefix}.${buyer?.rate || 0}.${status}`;
  }

  getStatusCode(buyer: Buyer): string {
    let status = '';
    if (buyer?.isActive === 'ACTIVE') {
      if (buyer?.isCommon) {
        status += 'C';
      }
      if (buyer?.isAddress) {
        status += 'A';
      }
      if (buyer?.isNumber) {
        status += 'N';
      }
      if (buyer?.isInspection) {
        status += 'I';
      }
      return status;
    }
    if (buyer?.address?.districtId) {
      status += 'S';
    }
    if (buyer?.address?.streetId) {
      status += 'S';
    }
    if (buyer?.address?.numberFrom) {
      status += 'N';
    }
    return status;
  }

  getRightBottomText(buyer: Buyer): string {
    const minPrice = buyer?.property?.minBuyPrice;
    const maxPrice = buyer?.property?.maxBuyPrice;
    if (minPrice || maxPrice) {
      const min = minPrice ? `$${StringUtils.formatNumber(minPrice)}` : '';
      const max = maxPrice ? `$${StringUtils.formatNumber(maxPrice)}` : '';
      return [min, max].filter((item) => item !== '').join('-');
    }
    return this.getStatusCode(buyer);
  }

  isPriceText(buyer: Buyer): boolean {
    return !!(buyer?.property?.minBuyPrice || buyer?.property?.maxBuyPrice);
  }
}
